import { useState } from 'react';

import { useGlobalSnackbar } from '@/contexts/snackContext';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useLocalization } from '@/hooks/useLocalization';
import ClientForm from '@/modules/clientServices/ClientForm';
import {
  buildEditInvestorPayload,
  ClientFormValues,
} from '@/modules/clientServices/clientForm.helpers';
import {
  useGetAdvisorInvestorSetupQuery,
  useUpdateAdvisorInvestorSetupMutation,
} from '@/store/usersApi';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

type EditInvestorFormProps = {
  onClose: () => void;
  userId: number;
  /** Shows a Cancel button next to Save. Hosts without their own close control pass it. */
  onCancel?: () => void;
};

const EditInvestorFlyout = ({
  userId,
  onClose,
  onCancel,
}: EditInvestorFormProps) => {
  const { t } = useLocalization();

  const authLoaded = useAuthIsLoaded();
  const { showSnackbar } = useGlobalSnackbar();

  /** DOM id linking an external submit control to the ClientForm's <form>. */
  const EDIT_CLIENT_FLYOUT_FORM = 'edit-client-flyout-form';

  // Data to load
  const { data, isLoading } = useGetAdvisorInvestorSetupQuery(
    authLoaded ? userId : skipToken,
  );
  const [updateInvestor] = useUpdateAdvisorInvestorSetupMutation();

  const [isValid, setIsValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setError] = useState(false);

  // Submission is owned here; ClientForm only emits the validated values.
  const handleSubmit = async (values: ClientFormValues) => {
    try {
      setIsSaving(true);
      setError(false);
      const payload = buildEditInvestorPayload(values);

      await updateInvestor({
        userId,
        investorId: data?.InvestorId,
        body: payload,
      }).unwrap();
      handleClose();
      showSnackbar(
        t('advisor.editClient.updateSuccess', { name: payload.Name }),
      );
    } catch {
      setError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setIsSaving(false);
    setError(false);
    onClose();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ py: 4 }}>
        {hasError && (
          <Alert
            severity='error'
            sx={{ mb: 3 }}
          >
            {t('advisor.editClient.updateFailure')}
          </Alert>
        )}

        <ClientForm
          submittedData={data}
          isSaving={isSaving}
          onSubmit={handleSubmit}
          onValidityChange={setIsValid}
          formId={EDIT_CLIENT_FLYOUT_FORM}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {onCancel ? (
          <Button
            variant='outlined'
            disabled={isSaving}
            onClick={onCancel}
          >
            {t('cancel', 'Cancel')}
          </Button>
        ) : null}
        <Button
          variant='contained'
          disabled={!isValid || isSaving}
          type='submit'
          form={EDIT_CLIENT_FLYOUT_FORM}
        >
          {isSaving ? t('saving', 'Saving...') : t('save', 'Save')}
        </Button>
      </Box>
    </>
  );
};

export default EditInvestorFlyout;
